import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AppLayout, AuthLayout } from '../layouts';
import { RequireRole } from '../components/RequireRole';
import { RequireAuth } from '../components/RequireAuth';

import { NotFoundPage } from './NotFoundPage';
import { MenuPage } from './MenuPage';

function lazyNamed<K extends string>(
  loader: () => Promise<Record<K, React.ComponentType<any>>>,
  name: K,
) {
  return lazy(() => loader().then((m) => ({ default: m[name] })));
}

// — auth (LoginPage from Phase 0; mfa/reset/locked rebuilt under iam)
const LoginPage = lazyNamed(() => import('./auth/LoginPage'), 'LoginPage');
const MfaPage = lazyNamed(() => import('./iam/auth/MfaChallengePage'), 'MfaChallengePage');
const PasswordResetRequestPage = lazyNamed(() => import('./iam/auth/ResetPasswordPage'), 'ResetPasswordPage');
const PasswordResetConfirmPage = lazyNamed(() => import('./iam/auth/ResetPasswordPage'), 'ResetPasswordPage');
const AccountLockedPage = lazyNamed(() => import('./iam/auth/AccountLockedPage'), 'AccountLockedPage');

// — shell / workspace picker (S1 — replaces brownfield DashboardPage as the landing)
const WorkspacePickerPage = lazyNamed(
  () => import('./shell/workspaces'),
  'WorkspacePickerPage',
);

// — shell / system-context screens (Part-7 §S4–S7, S10–S11)
const ComplianceExportsPage = lazyNamed(() => import('./shell/compliance-exports/ComplianceExportsPage'), 'ComplianceExportsPage');

// — out / lead-board (system-context: sourcing pipeline observability, spec §5.1 + §5.3)
const LeadBoardOverviewPage = lazyNamed(
  () => import('./out/lead-board'),
  'LeadBoardOverviewPage',
);
const LeadBoardBatchPage = lazyNamed(() => import('./out/lead-board'), 'LeadBoardBatchPage');
const LeadBoardChangelogPage = lazyNamed(() => import('./out/lead-board'), 'LeadBoardChangelogPage');
const LeadBoardNichePage = lazyNamed(() => import('./out/lead-board'), 'LeadBoardNichePage');
const LeadBoardBatchesPage = lazyNamed(() => import('./out/lead-board'), 'LeadBoardBatchesPage');

// — moa / system-context (audit log)

// — ntf (feed + delivery-log = system; templates + SLA settings = workspace context)
const NtfNotificationsFeedPage = lazyNamed(() => import('./ntf/feed/NotificationsFeedPage'), 'NotificationsFeedPage');
const NtfDeliveryLogPage = lazyNamed(() => import('./ntf/delivery-log/DeliveryLogPage'), 'DeliveryLogPage');
const NtfTemplateBuilderPage = lazyNamed(() => import('./ntf/templates/TemplateBuilderPage'), 'TemplateBuilderPage');
const NtfSlaSettingsPage = lazyNamed(() => import('./ntf/settings/SlaSettingsPage'), 'SlaSettingsPage');

// NOTE (v4 UI rebuild): the retired CRM/billing modules + the old out-split
// (outreach / out-emv / leads / icp) were deleted. Their screens are rebuilt
// under the v4 `out` module by the Phase-1 per-module workers. The routes below
// are the surviving brownfield module screens; they are rebuilt to the v4
// contract + Klaret in Phase 1 (per NAVIGATION_MAP + 08-ui-spec).

// — kh (workspace KB — AM-only; rebuilt from brownfield documents)
const KbLibraryPage = lazyNamed(() => import('./kh'), 'KbLibraryPage');
const KbEditorPage = lazyNamed(() => import('./kh'), 'KbEditorPage');

// — bi (workspace overview composite)
const WorkspaceOverviewPage = lazyNamed(() => import('./bi'), 'WorkspaceOverviewPage');

// — live board (kiosk/TV — full-bleed, rendered OUTSIDE AppLayout)
const LiveBoardPage = lazyNamed(() => import('./live-board'), 'LiveBoardPage');

// — com (admin AM-side client chat)
const ClientChatPage = lazyNamed(() => import('./com'), 'ClientChatPage');

// — ws-settings (workspace settings — tabbed: languages/autonomy/cooldown/limits/SLAs/AMs)
const WorkspaceSettingsPage = lazyNamed(
  () => import('./ws-settings/WorkspaceSettingsPage'),
  'WorkspaceSettingsPage',
);

// — out (lead pool [system] + campaigns/leads/sequences/templates/emails/calendars [workspace])
const PoolBrowserPage = lazyNamed(() => import('./out'), 'PoolBrowserPage');
const ColumnRegistryPage = lazyNamed(() => import('./out'), 'ColumnRegistryPage');
const ImportWizard = lazyNamed(() => import('./out'), 'ImportWizard');
const ImportHistoryPage = lazyNamed(() => import('./out'), 'ImportHistoryPage');
const LeadPoolSettingsPage = lazyNamed(() => import('./out'), 'LeadPoolSettingsPage');
const UnsuppressDialog = lazyNamed(() => import('./out'), 'UnsuppressDialog');
const PoolDeleteDialog = lazyNamed(() => import('./out'), 'PoolDeleteDialog');
const LeadsIcpsPage = lazyNamed(() => import('./out'), 'LeadsIcpsPage');
const IcpBuilder = lazyNamed(() => import('./out'), 'IcpBuilder');
const CampaignsListPage = lazyNamed(() => import('./out'), 'CampaignsListPage');
const ActiveCampaignsPage = lazyNamed(() => import('./out'), 'ActiveCampaignsPage');
const CampaignWizard = lazyNamed(() => import('./out'), 'CampaignWizard');
const CampaignOverviewPage = lazyNamed(() => import('./out'), 'CampaignOverviewPage');
const SequencesLibraryPage = lazyNamed(() => import('./out'), 'SequencesLibraryPage');
const SequenceEditor = lazyNamed(() => import('./out'), 'SequenceEditor');
const DynamicTemplateEditor = lazyNamed(() => import('./out'), 'DynamicTemplateEditor');
const EmailsDomainsPage = lazyNamed(() => import('./out'), 'EmailsDomainsPage');
const SendReviewPage = lazyNamed(() => import('./out'), 'SendReviewPage');
const SendHistoryPage = lazyNamed(() => import('./out'), 'SendHistoryPage');

// — aig (rebuilt: providers/confidence/cost/safety/observability)
const ProvidersPage = lazyNamed(() => import('./aig'), 'ProvidersPage');
const ConfidenceAutonomyPage = lazyNamed(() => import('./aig'), 'ConfidenceAutonomyPage');
const SafetyIncidentsPage = lazyNamed(() => import('./aig'), 'SafetyIncidentsPage');
const InjectionPatternsPage = lazyNamed(() => import('./aig'), 'InjectionPatternsPage');

// — iam / My Account (profile/security/preferences tabs) replaces brownfield settings
const MyAccountPage = lazyNamed(() => import('./iam/account/MyAccountPage'), 'MyAccountPage');
const ProfilePage = MyAccountPage;
const SecurityPage = MyAccountPage;
const PreferencesPage = MyAccountPage;
const ApiPage = MyAccountPage;
// — iam / admin CRUD + workspace lifecycle + approvals
const CreateUserPage = lazyNamed(() => import('./iam/users/CreateUserPage'), 'CreateUserPage');
const CreateWorkspacePage = lazyNamed(() => import('./iam/workspaces/CreateWorkspacePage'), 'CreateWorkspacePage');
const UnassignApprovalsPage = lazyNamed(() => import('./iam/approvals/UnassignApprovalsPage'), 'UnassignApprovalsPage');

// — admin — users/workspaces list+detail now from iam; roles/ops stay brownfield
const UsersListPage = lazyNamed(() => import('./iam/users/UsersListPage'), 'UsersListPage');
const UserDetailPage = lazyNamed(() => import('./iam/users/UserDetailPage'), 'UserDetailPage');
const WorkspacesListPage = lazyNamed(() => import('./iam/workspaces/WorkspacesListPage'), 'WorkspacesListPage');
const WorkspaceDetailPage = lazyNamed(() => import('./iam/workspaces/WorkspaceDetailPage'), 'WorkspaceDetailPage');
const IntegrationsPage = lazyNamed(
  () => import('./admin/integrations'),
  'IntegrationsPage',
);

// — lhd (workspace inbox/attention/agents/triage; legacy am-queue/bounces/handoffs dropped per module)
const InboxPage = lazyNamed(() => import('./lhd'), 'InboxPage');
const AttentionQueuePage = lazyNamed(() => import('./lhd'), 'AttentionQueuePage');
const AgentsListPage = lazyNamed(() => import('./lhd'), 'AgentsListPage');
const AgentDetailPage = lazyNamed(() => import('./lhd'), 'AgentDetailPage');
const ScriptsListPage = lazyNamed(() => import('./lhd'), 'ScriptsListPage');
const ScriptEditorPage = lazyNamed(() => import('./lhd'), 'ScriptEditorPage');
const DocumentsListPage = lazyNamed(() => import('./lhd'), 'DocumentsListPage');
const TriagePage = lazyNamed(() => import('./lhd'), 'TriagePage');
const BookingsListPage = lazyNamed(() => import('./lhd'), 'BookingsListPage');


// — grc (privacy/erasure only: suppression/dsar/retention; NIS2 governance register removed; detail = :id drawers)
const SuppressionListPage = lazyNamed(() => import('./grc'), 'SuppressionListPage');
const DsarQueuePage = lazyNamed(() => import('./grc'), 'DsarQueuePage');
const RetentionPage = lazyNamed(() => import('./grc'), 'RetentionPage');
const AuditLogPage = lazyNamed(() => import('./moa/audit-log/AuditLogPage'), 'AuditLogPage'); // moa owns audit log (was ./grc)

// — legal
const DataProcessingAgreementPage = lazyNamed(() => import('./legal'), 'DataProcessingAgreementPage');

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Suspense fallback={null}><LoginPage /></Suspense>} />
        <Route path="/auth/mfa" element={<Suspense fallback={null}><MfaPage /></Suspense>} />
        <Route path="/auth/password-reset" element={<Suspense fallback={null}><PasswordResetRequestPage /></Suspense>} />
        <Route path="/auth/password-reset/confirm" element={<Suspense fallback={null}><PasswordResetConfirmPage /></Suspense>} />
        <Route path="/auth/locked" element={<Suspense fallback={null}><AccountLockedPage /></Suspense>} />
      </Route>
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        {/* Post-login landing. Redirect to the canonical workspaces path so the nav
            section + subtab column resolve (findActiveSection matches no node for the
            bare "/", leaving the picker with no subtab and an unhighlighted sidebar). */}
        <Route path="/" element={<Navigate to="/shell/workspaces" replace />} />
        {/* Mobile navigation root — the Settings.app drill-down (see MenuPage). Two paths because
            workspace context is derived from the `/ws/` prefix: a single `/menu` would drop the
            user out of their workspace just by opening the menu. Desktop never routes here; it has
            the rail and the section column. */}
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/menu/:sectionId" element={<MenuPage />} />
        <Route path="/ws/menu" element={<MenuPage />} />
        <Route path="/ws/menu/:sectionId" element={<MenuPage />} />
        <Route path="/shell/workspaces" element={<Suspense fallback={null}><WorkspacePickerPage /></Suspense>} />
        {/* shell — system-context screens (S4–S7, S10–S11). Role-gating is UX; backend @Roles authoritative. */}
        <Route path="/shell/compliance/exports" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><ComplianceExportsPage /></RequireRole>} />
        {/* moa — audit log (surfaced under the Logs nav section) */}
        <Route path="/moa/audit-log" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><AuditLogPage /></RequireRole>} />
        {/* ntf — feed + delivery-log (system); templates + SLA settings (workspace context, reachable by URL; nav pass at wave-end) */}
        <Route path="/ntf/notifications" element={<NtfNotificationsFeedPage />} />
        <Route path="/ntf/delivery-log" element={<RequireRole roles={['admin', 'manager']}><NtfDeliveryLogPage /></RequireRole>} />
        <Route path="/ws/ntf/templates" element={<NtfTemplateBuilderPage />} />
        <Route path="/ws/ntf/templates/:templateId" element={<NtfTemplateBuilderPage />} />
        <Route path="/ws/ntf/settings/slas" element={<NtfSlaSettingsPage />} />
        {/* kh — workspace Knowledge Base (AM-only); seam components (SaveAnswerToKbModal, AmNotesPanel) imported by lhd/host screens at wave-end */}
        <Route path="/ws/kb" element={<KbLibraryPage />} />
        <Route path="/ws/kb/:docId" element={<KbEditorPage />} />
        {/* bi — workspace overview composite (BI owns analytics block; other blocks = labelled slots) + org analytics */}
        <Route path="/ws/overview" element={<WorkspaceOverviewPage />} />
        {/* live board — in-system Klaret dashboard (kiosk via in-page Fullscreen button) */}
        <Route path="/ws/live" element={<LiveBoardPage />} />
        <Route path="/ws/chat" element={<ClientChatPage />} />
        {/* ws-settings — tabbed workspace settings (languages/autonomy/cooldown/limits/SLAs/AMs) */}
        {/* Workspace Settings — one section per route so the secondary left
            sidebar (SectionSubtab) drives navigation, per the system norm. */}
        <Route path="/ws/settings" element={<Navigate to="/ws/settings/languages" replace />} />
        <Route path="/ws/settings/:section" element={<WorkspaceSettingsPage />} />
        {/* out — Lead Pool (system context) */}
        <Route path="/shell/lead-board" element={<RequireRole roles={['admin', 'manager', 'am']}><LeadBoardOverviewPage /></RequireRole>} />
        <Route path="/shell/lead-board/batches" element={<RequireRole roles={['admin', 'manager', 'am']}><LeadBoardBatchesPage /></RequireRole>} />
        <Route path="/shell/lead-board/batches/:batchId" element={<RequireRole roles={['admin', 'manager', 'am']}><LeadBoardBatchPage /></RequireRole>} />
        <Route path="/shell/lead-board/changelog" element={<RequireRole roles={['admin', 'manager', 'am']}><LeadBoardChangelogPage /></RequireRole>} />
        <Route path="/shell/lead-board/niches/:nicheId" element={<RequireRole roles={['admin', 'manager', 'am']}><LeadBoardNichePage /></RequireRole>} />
        <Route path="/shell/lead-pool" element={<RequireRole roles={['admin', 'manager']}><PoolBrowserPage /></RequireRole>} />
        <Route path="/shell/lead-pool/browser" element={<RequireRole roles={['admin', 'manager']}><PoolBrowserPage /></RequireRole>} />
        <Route path="/shell/lead-pool/columns" element={<RequireRole roles={['admin']}><ColumnRegistryPage /></RequireRole>} />
        <Route path="/shell/lead-pool/import" element={<RequireRole roles={['admin', 'am']}><ImportWizard /></RequireRole>} />
        <Route path="/shell/lead-pool/history" element={<RequireRole roles={['admin', 'manager', 'am']}><ImportHistoryPage /></RequireRole>} />
        <Route path="/shell/lead-pool/settings" element={<RequireRole roles={['admin']}><LeadPoolSettingsPage /></RequireRole>} />
        <Route path="/shell/lead-pool/browser/people/:personId/unsuppress" element={<RequireRole roles={['admin']}><UnsuppressDialog /></RequireRole>} />
        <Route path="/shell/lead-pool/browser/:list/:recordId/delete" element={<RequireRole roles={['admin']}><PoolDeleteDialog /></RequireRole>} />
        {/* out — workspace context (campaigns / leads&ICPs / sequences / templates / emails&domains / calendars) */}
        <Route path="/ws/leads" element={<LeadsIcpsPage />} />
        {/* :icpId carries the literal 'new' sentinel for create mode — a separate
            static /ws/icps/new route would shadow this and leave useParams().icpId
            undefined, flipping the builder into (broken) edit mode. */}
        <Route path="/ws/icps/:icpId" element={<IcpBuilder />} />
        <Route path="/ws/campaigns" element={<CampaignsListPage />} />
        <Route path="/ws/campaigns/active" element={<ActiveCampaignsPage />} />
        <Route path="/ws/campaigns/new" element={<CampaignWizard />} />
        <Route path="/ws/campaigns/:draftId/edit" element={<CampaignWizard />} />
        <Route path="/ws/campaigns/:campaignId" element={<CampaignOverviewPage />} />
        <Route path="/ws/sequences" element={<SequencesLibraryPage />} />
        {/* :sequenceId carries the 'new' sentinel — see the /ws/icps note above. */}
        <Route path="/ws/sequences/:sequenceId" element={<SequenceEditor />} />
        {/* :templateId carries the 'new' sentinel, same as the sequence editor above. A
            combination's EMAILS are edited through /ws/sequences/:id — a combination is a
            sequence — so this route only decides who gets which set. */}
        <Route path="/ws/dynamic-templates/:templateId" element={<DynamicTemplateEditor />} />
        <Route path="/ws/inboxes" element={<EmailsDomainsPage />} />
        <Route path="/ws/sending/review" element={<SendReviewPage />} />
        <Route path="/ws/sending/outbound" element={<SendHistoryPage />} />
        {/* aig — system AI admin */}
        <Route path="/aig/providers" element={<RequireRole roles={['admin', 'manager']}><ProvidersPage /></RequireRole>} />
        <Route path="/aig/confidence-autonomy" element={<RequireRole roles={['admin', 'manager']}><ConfidenceAutonomyPage /></RequireRole>} />
        <Route path="/aig/safety" element={<RequireRole roles={['admin', 'manager']}><SafetyIncidentsPage /></RequireRole>} />
        <Route path="/aig/safety/patterns" element={<RequireRole roles={['admin', 'manager']}><InjectionPatternsPage /></RequireRole>} />
        <Route path="/settings/profile" element={<ProfilePage />} />
        <Route path="/settings/security" element={<SecurityPage />} />
        <Route path="/settings/preferences" element={<PreferencesPage />} />
        <Route path="/settings/api" element={<ApiPage />} />
        <Route path="/admin/users" element={<UsersListPage />} />
        <Route path="/admin/users/:id" element={<UserDetailPage />} />
        <Route path="/admin/workspaces" element={<WorkspacesListPage />} />
        <Route path="/admin/workspaces/:id" element={<WorkspaceDetailPage />} />
        {/* iam — create flows, account, AM-unassign approvals, impersonation */}
        <Route path="/admin/users/new" element={<RequireRole roles={['admin']}><CreateUserPage /></RequireRole>} />
        <Route path="/shell/users/new" element={<RequireRole roles={['admin']}><CreateUserPage /></RequireRole>} />
        <Route path="/admin/workspaces/new" element={<RequireRole roles={['admin']}><CreateWorkspacePage /></RequireRole>} />
        <Route path="/shell/workspaces/new" element={<RequireRole roles={['admin']}><CreateWorkspacePage /></RequireRole>} />
        <Route path="/iam/account" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/iam/attention/unassign-approvals" element={<RequireRole roles={['admin', 'manager']}><UnassignApprovalsPage /></RequireRole>} />
        <Route path="/admin/integrations" element={<IntegrationsPage />} />
        {/* lhd — workspace inbox/attention/agents/triage/bookings (legacy am-queue/bounces/handoffs dropped per module) */}
        <Route path="/ws/bookings" element={<BookingsListPage />} />
        <Route path="/ws/inbox" element={<InboxPage />} />
        <Route path="/ws/inbox/:conversationId" element={<InboxPage />} />
        <Route path="/ws/attention" element={<AttentionQueuePage />} />
        <Route path="/ws/attention/:cardId" element={<AttentionQueuePage />} />
        <Route path="/ws/agents" element={<AgentsListPage />} />
        <Route path="/ws/agents/:agentId" element={<AgentDetailPage />} />
        <Route path="/ws/scripts" element={<ScriptsListPage />} />
        <Route path="/ws/scripts/:scriptId" element={<ScriptEditorPage />} />
        <Route path="/ws/documents" element={<DocumentsListPage />} />
        <Route path="/lhd/triage" element={<TriagePage />} />
        {/* grc — privacy/erasure area (NIS2 governance register removed; detail views are :id drawers) */}
        <Route path="/grc" element={<Navigate to="/grc/dsar" replace />} />
        <Route path="/grc/suppression" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><SuppressionListPage /></RequireRole>} />
        <Route path="/grc/suppression/:id" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><SuppressionListPage /></RequireRole>} />
        <Route path="/grc/dsar" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><DsarQueuePage /></RequireRole>} />
        <Route path="/grc/dsar/:id" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><DsarQueuePage /></RequireRole>} />
        <Route path="/grc/retention" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><RetentionPage /></RequireRole>} />
        <Route path="/grc/retention/:id" element={<RequireRole roles={['admin', 'manager', 'compliance_reviewer']}><RetentionPage /></RequireRole>} />
        <Route path="/legal/dpa" element={<DataProcessingAgreementPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
